'use client';

import Link from 'next/link';
import Image from 'next/image';
import { HomeIcon, ImagePlus, MessageSquareIcon, WalletIcon, UserIcon, MoreVerticalIcon, LogOutIcon, MenuIcon, SearchIcon, Loader2, Bold } from 'lucide-react';
import { useRouter, usePathname } from 'next/navigation';
import { useState, useEffect, useRef } from 'react';
import { AdvancedImage } from '@cloudinary/react';

import SubscriptionButton from '@/app/tab/shared/buttons/subscription-button';
import { useUserAppData } from '@/app/user-and-session/user-app-data/useUserAppData';
import { useLogout } from '@/app/user-and-session/session-auth/hooks/useLogout';
import { useRecentChats } from '@/app/recent-chat/shared/useRecentChats';
import type { RecentChatAvatarWithUrl } from '@/app/recent-chat/shared/actions';
import { useNavbar } from '@/app/lib/contexts/NavbarContext';
import LoginPopup from '@/app/popup/login-popup';
import { useAuth } from '@/app/user-and-session/session-auth/hooks/useAuth';
import '@/app/ui/scrollbar.css';
import { initCloudinaryWithPreloadedConfig } from '@/app/lib/cloudinary/cloudinary-image-utils';
import LanguageSelectPopup from '@/app/user-and-session/languages/ui/LanguageSelectPopup';
import { useTranslations } from 'next-intl';
import { useLocale, locales } from '@/app/user-and-session/languages/i18n';
import SUPPORTED_LANGUAGES from '@/app/user-and-session/languages/languages';

type NavItem = {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  href: string;
  fontFamily?: string;
};



export default function LandscapeSideNav() {
  const { session, userEmail, userName, isLoading: authLoading, isAuthenticated } = useAuth();
  initCloudinaryWithPreloadedConfig('dango-cloud'); // warmup cloudinary for image cdn
  
  // Use the new user app data hook - it will handle empty userEmail gracefully
  const [userData, userActions] = useUserAppData(
    !authLoading && isAuthenticated && userEmail ? userEmail : ''
  );
  
  const router = useRouter();
  const pathname = usePathname();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [loginRedirectUrl, setLoginRedirectUrl] = useState('/');
  const [showLanguagePopup, setShowLanguagePopup] = useState(false);
  const t = useTranslations('nav');
  const { locale } = useLocale();
  const languageName = SUPPORTED_LANGUAGES[locale] || SUPPORTED_LANGUAGES['en'];
  const { isCollapsed, setIsCollapsed } = useNavbar();
  const userButtonRef = useRef<HTMLButtonElement>(null);
  const moreIconRef = useRef<SVGSVGElement>(null);
  const { logout } = useLogout();
  
  // Use recent chats hook directly instead of context
  const { recentChats, isLoadingChats, refreshRecentChats } = useRecentChats();
  

  const mainNavItems: NavItem[] = [
    {
      icon: HomeIcon,
      label: t('discover'),
      href: "/discover",
      fontFamily: "Montserrat"
    },
    {
      icon: ImagePlus,
      label: t('create'),
      href: "/image-generation",
      fontFamily: "Inter"
    },
    {
      icon: UserIcon,
      label: t('profile'),
      href: "/profile",
      fontFamily: "Inter"
    },
  ];

  const handleNavClick = (href: string, label: string) => {
    // Check if it's the Create button (works with any language)
    if (href === "/image-generation") {
      if (!session) {
        setLoginRedirectUrl('/image-generation');
        setShowLoginPopup(true);
        return;
      }
    }
    
    // Trigger chat history refresh when navigating to Home/Discover
    if (href === "/discover" || href === "/") {
      refreshRecentChats();
    }
    
    router.push(href);
  };

  const isActive = (href: string) => {
    if (href === "/" && pathname === "/") return true;
    if (href !== "/" && pathname.startsWith(href)) return true;
    return false;
  };

  const getUserInitial = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  const calculateMenuPosition = () => {
    if (userButtonRef.current) {
      const buttonRect = userButtonRef.current.getBoundingClientRect();
      
      const left = buttonRect.right + 8;
      const top = buttonRect.bottom - 224; // 224px = 4 buttons × 56px height
      
      setMenuPosition({ top, left });
    }
  };

  const handleUserMenuToggle = () => {
    if (!showUserMenu) {
      calculateMenuPosition();
    }
    setShowUserMenu(!showUserMenu);
  };

  return (
    <nav className={`flex flex-col h-screen items-start bg-[#121214] transition-all duration-300 ease-in-out ${isCollapsed ? 'w-20' : 'w-64'} flex-shrink-0`}>
      {/* Header with logo/menu button */}
      <div className={`flex items-center w-full transition-all duration-300 ease-in-out ${isCollapsed ? 'justify-center px-2 py-4 2xl:py-10' : 'pl-4 pr-2 py-4'}`}>
        {isCollapsed ? (
          <button
            onClick={() => setIsCollapsed(false)}
            className="flex items-center justify-center w-12 h-12 text-white hover:bg-[#ffffff1a] rounded-xl transition-colors"
          >
            <MenuIcon className="w-6 h-6" />
          </button>
        ) : (
          <>
            <button
              onClick={() => {
                console.log('Logo clicked - refreshing chat history');
                refreshRecentChats();
                router.push('/');
              }}
              className="flex items-center overflow-hidden"
            >
              <div className="w-47 h-9 overflow-hidden flex-shrink-0">
                <Image
                  src="/logo_dango_cropped.png"
                  alt="Logo"
                  width={160}
                  height={24}
                  priority
                  className="object-cover object-center w-full h-full transition-all duration-300 ease-in-out"
                />
              </div>
            </button>
            <button
              onClick={() => setIsCollapsed(true)}
              className="flex items-center justify-center w-8 h-8 text-white hover:bg-[#ffffff1a] rounded-xl transition-colors ml-auto"
            >
              <MenuIcon className="w-6 h-6" />
            </button>
          </>
        )}
      </div>

      {/* Separator */}
      <div className="w-full h-[1px] bg-[#8f909240]" />

      {/* Navigation menu */}
      <div className={`flex flex-col items-start gap-2 w-full transition-all duration-300 ease-in-out ${isCollapsed ? 'px-2' : 'px-4 2xl:px-6'} py-2 2xl:py-6`}>
        {mainNavItems.map((item, index) => {
          const IconComponent = item.icon;
          const active = isActive(item.href);
          
          // Hide Profile when not logged in (check by href since label is translated)
          if (item.href === "/profile" && !session) {
            return null;
          }
          
          return (
            <div key={item.label} className="w-full">
              <button
                onClick={() => handleNavClick(item.href, item.label)}
                className={`flex items-center gap-3 w-full rounded-[12px] h-auto transition-colors duration-200 group ${active ? "bg-[#ffffff1a]" : ""} hover:bg-[#ffffff1a] ${isCollapsed ? 'justify-center px-2 py-3' : 'justify-start px-3 py-2.5 2xl:py-3.5'}`}
                title={isCollapsed ? item.label : undefined}
              >
                <IconComponent className={`w-6 h-6 ${active ? "text-white" : "text-[#8f9092] group-hover:text-white"} ${isCollapsed ? '' : ''}`} />
                {!isCollapsed && (
                  <span
                    className={`font-medium text-base tracking-[-0.32px] leading-5 text-left flex-1 ${active ? "text-white" : "text-[#8f9092] group-hover:text-white"} ${item.fontFamily === "Montserrat" ? "[font-family:'Montserrat',Helvetica]" : "[font-family:'Inter',Helvetica]"}`}
                  >
                    {item.label}
                  </span>
                )}
              </button>
            </div>
          );
        })}
        
        {/* Subscription Button */}
        <SubscriptionButton isCollapsed={isCollapsed} />
      </div>

      {/* Recent Chats Section */}
      {session && recentChats.length > 0 && (
        <>
          {/* Separator */}
          <div className="w-full h-[1px] bg-[#8f909240]" />
          
          <div className={`flex flex-col w-full flex-1 min-h-0 transition-all duration-300 ease-in-out ${isCollapsed ? 'px-2' : 'px-4 2xl:px-6'} py-2`}>
            {!isCollapsed && (
              <div className="flex items-center justify-between mb-2 flex-shrink-0">
                <h3 
                  className="text-[#8f9092] text-sm font-medium [font-family:'Montserrat',Helvetica] cursor-pointer hover:text-white transition-colors duration-200"
                  onClick={() => router.push('/my-chat')}
                >
                  {t('recentChats')}
                </h3>
              </div>
            )}
            
            <div className={`flex flex-col gap-2 flex-1 overflow-y-auto dark-scrollbar ${isCollapsed ? 'items-center' : ''}`}>
              {recentChats.map((chat: RecentChatAvatarWithUrl) => (
                <a
                  key={chat.avatar_id}
                  href={`/chat/${chat.avatar_id}`}
                  className={`flex items-center gap-3 w-full rounded-[12px] h-auto transition-colors duration-200 group hover:bg-[#ffffff1a] flex-shrink-0 ${isCollapsed ? 'justify-center px-2 py-2' : 'justify-start px-3 py-2'}`}
                  title={isCollapsed ? chat.avatar_name : undefined}
                >
                  {/* Avatar Image */}
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 border border-[#d9d9d9]">
                    {chat.cloudinaryImage ? (
                      <AdvancedImage
                        cldImg={chat.cloudinaryImage}
                        alt={chat.avatar_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-[#2a2a2e] flex items-center justify-center">
                        <span className="text-white text-xs font-medium">
                          {chat.avatar_name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {!isCollapsed && (
                    <div className="flex flex-col items-start justify-center flex-1 min-w-0">
                      <span className="text-white text-sm font-medium [font-family:'Montserrat',Helvetica] truncate w-full text-left">
                        {chat.avatar_name}
                      </span>
                    </div>
                  )}
                </a>
              ))}
            </div>
          </div>
        </>
      )}

      {/* User profile section at bottom */}
      <div className={`flex flex-col items-center justify-end gap-2 w-full mt-auto transition-all duration-300 ease-in-out ${isCollapsed ? 'px-2' : 'px-6'} py-2`}>
        {session ? (
          <div className="relative w-full">
            <button
              onClick={handleUserMenuToggle}
              className={`flex w-full items-center justify-center gap-3 bg-[#ffffff1a] rounded-xl hover:bg-[#ffffff20] transition-colors ${isCollapsed ? 'h-12 px-2 py-2' : 'h-[54px] px-3 py-3.5'}`}
              ref={userButtonRef}
              title={isCollapsed ? (authLoading || !userData.isInitialized ? '' : (userData.userPreferredName || userName || '')) : undefined}
            >
              {/* Avatar */}
              <div className="w-8 h-8 border-2 border-solid border-gray-400 rounded-full bg-[#2a2a2e] flex items-center justify-center">
                {authLoading || !userData.isInitialized ? (
                  <></>
                ) : (
                  <span className="text-gray-400 text-xs font-medium">
                    {getUserInitial(userData.userPreferredName || userName || '')}
                  </span>
                )}
              </div>

              {!isCollapsed && (
                <>
                  {/* User name */}
                  <div className="flex flex-col items-start gap-1 flex-1">
                    {authLoading || !userData.isInitialized ? (
                      <div className="h-3 w-20 bg-gray-600 rounded animate-pulse"></div>
                    ) : (
                      <span className="font-normal text-xs leading-4 text-white [font-family:'Montserrat',Helvetica] truncate max-w-[120px]">
                        {userData.userPreferredName || userName || ''}
                      </span>
                    )}
                  </div>

                  {/* Menu button */}
                  <MoreVerticalIcon className="w-6 h-6 text-white" ref={moreIconRef} />
                </>
              )}
            </button>

            {/* Dropdown Menu */}
            {showUserMenu && (
              <div 
                className="fixed min-w-[220px] bg-[#2f363e] rounded-xl shadow-2xl z-[9999] overflow-hidden"
                style={{
                  top: `${menuPosition.top}px`,
                  left: `${menuPosition.left}px`
                }}
              >
                <div className="space-y-0">
                  {/* Language Selection Button */}
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      setShowLanguagePopup(true);
                    }}
                    className="flex items-center justify-between w-full h-14 px-4 text-white hover:bg-[#ffffff1a] transition-colors border-b border-gray-700"
                  >
                    <span className="text-sm font-medium font-['Montserrat',Helvetica]">
                      {t('language')}: {languageName}
                    </span>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  {/* Terms of Service Button */}
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      window.open('/terms-of-service', '_blank');
                    }}
                    className="flex items-center justify-between w-full h-14 px-4 text-white hover:bg-[#ffffff1a] transition-colors border-b border-gray-700"
                  >
                    <span className="text-sm font-medium font-['Montserrat',Helvetica]">
                      {t('termsOfService')}
                    </span>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  {/* Privacy Policy Button */}
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      window.open('/privacy', '_blank');
                    }}
                    className="flex items-center justify-between w-full h-14 px-4 text-white hover:bg-[#ffffff1a] transition-colors border-b border-gray-700"
                  >
                    <span className="text-sm font-medium font-['Montserrat',Helvetica]">
                      {t('privacyPolicy')}
                    </span>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  {/* Logout Button */}
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      logout();
                    }}
                    className="flex items-center justify-between w-full h-14 px-4 text-red-400 hover:bg-[#ffffff1a] transition-colors"
                  >
                    <span className="text-sm font-medium font-['Montserrat',Helvetica]">
                      {t('logout')}
                    </span>
                    <LogOutIcon className="w-5 h-5 text-red-400" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          !isCollapsed && (
            <div className="flex flex-col gap-2 w-full">
                <button
                  onClick={() => {
                    setLoginRedirectUrl('/');
                    setShowLoginPopup(true);
                  }}
                  className={`flex items-center gap-3 w-full rounded-[12px] h-auto transition-colors duration-200 group bg-[#5856d6] hover:bg-[#5856d6] justify-start px-3 py-2.5 2xl:py-3.5`}
                >
                  <UserIcon className="w-6 h-6 text-white" />
                  <span className="font-medium text-base tracking-[-0.32px] leading-5 text-left flex-1 text-white [font-family:'Montserrat',Helvetica]">
                    {t('signIn')}
                  </span>
                </button>
            </div>
          )
        )}
      </div>

      {/* Blog links section */}
      <div className={`w-full px-4 2xl:px-6 pb-2 ${isCollapsed ? 'hidden' : ''}`}>
        <div className="text-gray-400 text-[10px] leading-tight">
          <a 
            href="/about-us" 
            target="_blank" 
            rel="noopener noreferrer"
            className="hover:underline"
          >
            {t('aboutUs')}
          </a>&nbsp;&nbsp;|&nbsp;&nbsp;
          <a 
            href="/terms-of-service" 
            target="_blank" 
            rel="noopener noreferrer"
            className="hover:underline"
          >
            {t('termsOfService')}
          </a>&nbsp;&nbsp;|&nbsp;&nbsp;
          <a 
            href="/community-guidelines" 
            target="_blank" 
            rel="noopener noreferrer"
            className="hover:underline"
          >
            {t('communityGuidelines')}
          </a>&nbsp;&nbsp;|&nbsp;&nbsp;
          <a 
            href="/privacy" 
            target="_blank" 
            rel="noopener noreferrer"
            className="hover:underline"
          >
           &nbsp;&nbsp;{t('privacyPolicy')}
          </a>
        </div>
      </div>

      {/* Click outside to close menu */}
      {showUserMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowUserMenu(false)}
        />
      )}
      
      {/* Login Popup */}
      <LoginPopup 
        isOpen={showLoginPopup} 
        onClose={() => {
          setShowLoginPopup(false);
        }}
        redirectTarget={loginRedirectUrl}
      />

      {/* Language Selection Popup */}
      <LanguageSelectPopup
        isOpen={showLanguagePopup}
        onClose={() => setShowLanguagePopup(false)}
      />
    </nav>
  );
}